"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
const faqItems = [{
  id: "item-1",
  question: "What happens during an initial consultation?",
  answer: "We start by listening to your specific financial situation, family goals, and concerns. We review any current policies or retirement accounts you hold, explain how different insurance or annuity structures work in plain language, and answer your questions. There is no obligation to purchase anything."
}, {
  id: "item-2",
  question: "How are your consulting and advisory services compensated?",
  answer: "Our educational consultations and policy reviews are provided at no direct charge to you. When you choose to implement a strategy through an insurance carrier or annuity provider, we are compensated by the carrier. We maintain full transparency regarding all product fees, surrender periods, and carrier ratings."
}, {
  id: "item-3",
  question: "Are you an independent practice or tied to a single company?",
  answer: "DJTD Consulting operates independently. We do not work for a single insurance company or bank. This independence allows us to evaluate products across dozens of highly rated national carriers to find options that align precisely with your retirement timeline and risk comfort."
}, {
  id: "item-4",
  question: "Can we meet remotely or do we need to meet in person?",
  answer: "We offer both secure video conferences and phone consultations for clients nationwide, as well as in-person meetings at our Anytown office. All documents and educational visual summaries can be shared digitally for review at your own pace."
}, {
  id: "item-5",
  question: "Do you provide ongoing reviews after a policy is established?",
  answer: "Yes. Long-term understanding is central to our practice. We schedule annual reviews to ensure your coverage, annuity guarantees, and beneficiary designations stay aligned as your family circumstances or retirement timelines change."
}];
export default function Consulting() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="working-with-djtd-consulting" data-nav="light" className="bg-background py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mb-12 max-w-[840px] text-center md:mb-16">
          <div className="mb-3.5 inline-flex items-center gap-1.5">
            <Badge variant="outline" className="border-primary/20 bg-muted px-3 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
              Working with us
            </Badge>
          </div>

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-[2.65rem]">
            Working with DJTD Consulting
          </h2>

          <div className="mx-auto my-4 flex items-center justify-center gap-1.5">
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
          </div>

          <p className="font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            Clear answers about how we evaluate options, explain policies, and protect your plans without sales pressure.
          </p>
        </motion.div>

        {/* Accordion Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mx-auto max-w-[840px]">
          <Accordion type="single" collapsible className="w-full space-y-3.5">
            {faqItems.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i}>
                <AccordionItem value={item.id} className="rounded-lg border border-border bg-card px-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30 md:px-6">
                  <AccordionTrigger className="py-5 text-left text-base font-semibold text-card-foreground hover:no-underline sm:text-lg">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    <div className="pl-8 border-l border-border/70 ml-2">
                      {item.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>

          {/* Bottom Guidance Plinth */}
          <motion.div variants={itemVariants} className="mt-10 rounded-xl border border-border bg-card p-6 shadow-xs sm:p-8">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                <h3 className="font-default text-lg font-semibold text-card-foreground sm:text-xl">
                  Have a specific question about your plan?
                </h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                Schedule a focused conversation with a licensed professional to review your options.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}